import React from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { Button } from '@rezi/ui/button';
import { Loading } from '@rezi/ui/loading';
import { AuthProvider, useAuth } from './auth';
import { DocsVisibilityProvider } from './docsVisibility';
import DocsIndex from './pages/DocsIndex';
import CategoryPage from './pages/CategoryPage';
import DocPage from './pages/DocPage';
import LoginPage from './pages/LoginPage';
import DocsSearch from './components/DocsSearch';
import { ReactComponent as Logo } from './assets/img/logo-dark.svg';
import APIDoc from './pages/APIDoc';

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div>
      <div id="doc-search-bar">
        <DocsSearch />
      </div>
      {children}
    </div>
  );
};

const HeaderContent = () => {
  const { user, signOutUser } = useAuth();

  return (
    <div className="mx-auto flex max-w-6xl flex-row items-center justify-between gap-3 px-6 py-5">
      <div className="relative flex flex-col items-center justify-center">
        <Logo className="h-11 w-auto" />
        <span className="absolute top-[30px] left-[39px] font-semibold text-[#2d3e50]">
          Documentation
        </span>
      </div>
      <div className="flex items-center gap-3">
        {user && (
          <div className="hidden text-xs text-gray-500 sm:block">
            {user.email}
          </div>
        )}
        {user && (
          <Button
            label="Sign out"
            theme="secondary"
            size="xs"
            onClick={signOutUser}
          />
        )}
      </div>
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <DocsVisibilityProvider>
        <div className="bg-background min-h-screen">
          <header className="sticky top-0 z-10 backdrop-blur">
            <HeaderContent />
          </header>
          <main className="mx-auto w-full max-w-6xl px-6 py-8">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <DocsIndex />
                  </RequireAuth>
                }
              />
              <Route
                path="/docs/:docId"
                element={
                  <RequireAuth>
                    <DocPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/category/:categoryId"
                element={
                  <RequireAuth>
                    <CategoryPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/apis/:docName"
                element={
                  <RequireAuth>
                    <APIDoc />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </DocsVisibilityProvider>
    </AuthProvider>
  );
};

export default App;
